import React, {
    useContext,
    useMemo
} from 'react';
import _ from 'lodash';
import SpmConstants from 'sg-spm-constants';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { CurrencyValue } from '@jutro/components';
import { SgDateUtil } from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import messages from './SgEndorsementChangesInfo.messages';

function SgEndorsementChangesInfo(props) {
    const {
        modelVM,
        changes,
        getValue
    } = props;

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const contactUsPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_VEHICLE_ALTERNATIVE_ADDRESS_PHONE_NUMBER;
    const { hasVehicleChanges, hasDriverChanges, hasCoveragesChanges } = modelVM.lobData.privateMotor.selectedChanges.value;
    const hasExcessChanges = !_.isNil(changes.voluntaryExcess) || !_.isNil(changes.compulsoryExcess) || !_.isNil(changes.fireAndTheftExcess);

    // Vehicle changes
    const { spmVehicle } = modelVM.lobData.privateMotor.coverables;
    const getModificationString = ((modifications) => {
        let modificationString = '';
        _.each(modifications, (mod, index) => {
            if (index > 0) {
                modificationString += ', ';
            }
            modificationString += mod;
        });
        return modificationString;
    });

    const vehicleChanges = useMemo(() => {
        return (
            <ul className="sg-mta-changes-info-details-list">
                <h4>{translator(messages.VehicleChanges)}</h4>
                {!_.isNil(changes.vehicle) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.VehicleChange)}</b>
                        <p>{`${changes.vehicle.registration}, ${changes.vehicle.make}, ${changes.vehicle.model}`}</p>
                    </div>
                )}
                {!_.isNil(changes.registrationNumber) && (
                    <>
                        <div className="sg-flex-div">
                            <b>{translator(messages.RegChange)}</b>
                            <p>{changes.registrationNumber }</p>
                        </div>
                        <div className="sg-flex-div">
                            <b>{translator(messages.OnExistingVehicle)}</b>
                            <p>{translator(messages.ExistingVehicle, { make: spmVehicle.value.make, model: spmVehicle.value.model })}</p>
                        </div>
                    </>
                )}
                {!_.isNil(changes.addedModification) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.ModificationAdded)}</b>
                        <p>{getModificationString(changes.addedModification)}</p>
                    </div>
                )}
                {!_.isNil(changes.removedModification) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.ModificationRemoved)}</b>
                        <p>{getModificationString(changes.removedModification)}</p>
                    </div>
                )}
                {!_.isNil(changes.mileage) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.MileageChange)}</b>
                        <p>{getValue(changes.mileage)}</p>
                    </div>
                )}
                {!_.isNil(changes.vehicleUse) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.VehicleUseChange)}</b>
                        <p>{getValue(changes.vehicleUse)}</p>
                    </div>
                )}
                {!_.isNil(changes.vehicleParkingType) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.VehicleParkingChange)}</b>
                        <p>{getValue(changes.vehicleParkingType)}</p>
                    </div>
                )}
            </ul>
        );
    }, [changes.addedModification, changes.mileage, changes.registrationNumber, changes.removedModification, changes.vehicle, changes.vehicleUse,
        changes.vehicleParkingType, getValue, spmVehicle.value.make, spmVehicle.value.model, translator]);

    // Driver changes
    const driverChanges = useMemo(() => {
        return (
            <ul className="sg-mta-changes-info-details-list">
                <h4>{translator(messages.DriverChanges)}</h4>
                {!_.isNil(changes.addedDriver) && (
                    <>
                        <div className="sg-flex-div">
                            <b>{translator(messages.DriverAdded)}</b>
                            <p>
                                {`${changes.addedDriver.person.firstName} ${changes.addedDriver.person.lastName}, 
                                    DOB: ${SgDateUtil.toStringShort(changes.addedDriver.person.dateOfBirth)}`}
                            </p>
                        </div>
                        <p>
                            {translator(messages.AlternativeAddress, {
                                phoneNumber: isMobile
                                    ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a>
                                    : contactUsPhoneNumber
                            })}
                        </p>
                    </>
                )}
                {!_.isNil(changes.removedDriver) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.DriverRemoved)}</b>
                        <p>
                            {`${changes.removedDriver.person.firstName} ${changes.removedDriver.person.lastName}, 
                                DOB: ${SgDateUtil.toStringShort(changes.removedDriver.person.dateOfBirth)}`}
                        </p>
                    </div>
                )}
                {!hasVehicleChanges && !_.isNil(changes.mileage) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.MileageChange)}</b>
                        <p>{getValue(changes.mileage)}</p>
                    </div>
                )}
                {!hasVehicleChanges && !_.isNil(changes.vehicleUse) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.VehicleUseChange)}</b>
                        <p>{getValue(changes.vehicleUse)}</p>
                    </div>
                )}
            </ul>
        );
    }, [translator, changes.addedDriver, changes.removedDriver, changes.mileage, changes.vehicleUse, isMobile, contactUsPhoneNumber,
        hasVehicleChanges, getValue]);

    // Coverages changes
    const { coverages } = modelVM.value.lobData.privateMotor.offerings[0].coverages.vehicleCoverages[0];
    const currentTerms = coverages.find((element) => element.patternCode === SpmConstants.BASIC_MOTOR_COVER_CODE).terms;
    const currentCompulsoryExcessValue = currentTerms.find((term) => term.patternCode === SpmConstants.COMPULSORY_AD_EXCESS_CODE).directValue;
    const currentVoluntaryExcess = currentTerms.find((term) => term.patternCode === SpmConstants.VOLUNTARY_AD_EXCESS_CODE);
    const currentVolExcessValue = currentVoluntaryExcess.options
        .find((option) => option.name === currentVoluntaryExcess.chosenTermValue).originalValueExt;

    const excessHeaders = useMemo(() => {
        return (
            <>
                {
                    !_.isNil(changes.fireAndTheftExcess)
                        ? (
                            <div>
                                {translator(messages.ExcessTableHeaderFT, { excess: changes.fireAndTheftExcess })}
                            </div>
                        )
                        : null
                }
                {
                    !_.isNil(changes.compulsoryExcess)
                        ? (
                            <div>
                                {translator(messages.ExcessTableHeader,
                                    { compulsory: changes.compulsoryExcess ? 'compulsory' : '', excess: changes.compulsoryExcess })}
                            </div>
                        )
                        : null
                }
            </>
        );
    }, [changes.compulsoryExcess, changes.fireAndTheftExcess, translator]);

    const coveragesAdded = useMemo(() => {
        let coveragesAddedString = '';
        _.each(changes.coverageAdded, (coverage, index) => {
            if (index > 0) {
                coveragesAddedString += ', ';
            }
            coveragesAddedString += coverage.name;
        });
        return coveragesAddedString;
    }, [changes.coverageAdded]);

    const coveragesRemoved = useMemo(() => {
        let coveragesRemovedString = '';
        _.each(changes.coverageRemoved, (coverage, index) => {
            if (index > 0) {
                coveragesRemovedString += ', ';
            }
            coveragesRemovedString += coverage.name;
        });
        return coveragesRemovedString;
    }, [changes.coverageRemoved]);

    const coveragesChanges = useMemo(() => {
        return (
            <ul className="sg-mta-changes-info-details-list">
                <h4>{translator(messages.CoverOptionChanges)}</h4>
                {!_.isNil(changes.coverageAdded) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.CoverageAdded)}</b>
                        <p>{coveragesAdded}</p>
                    </div>
                )}
                {!_.isNil(changes.coverageRemoved) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.CoverageRemoved)}</b>
                        <p>{coveragesRemoved}</p>
                    </div>
                )}
                {!_.isNil(changes.breakdownCoverLevel) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.Changed)}</b>
                        <p>{translator(messages.BreakdownCoverChanged, { coverLevel: changes.breakdownCoverLevel })}</p>
                    </div>
                )}
                {!_.isNil(changes.voluntaryExcess) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.Changed)}</b>
                        <p>{translator(messages.VoluntaryExcessChanged, { excess: changes.voluntaryExcess })}</p>
                    </div>
                )}
                {!_.isNil(changes.pncdPrice) && (
                    <div className="sg-flex-div">
                        <b>{translator(messages.Changed)}</b>
                        <p>{translator(messages.PNCDPriceChanged, { amount: changes.pncdPrice })}</p>
                    </div>
                )}
                {hasExcessChanges && (
                    <>
                        {excessHeaders}
                        {(!_.isNil(changes.compulsoryExcess) || !_.isNil(changes.voluntaryExcess)) && (
                            <>
                                <div>
                                    <p>
                                        {translator(messages.Header)}
                                    </p>
                                </div>
                                <table className="cepExcessesChangesTable">
                                    <tbody>
                                        <tr>
                                            <td>{translator(messages.VoluntaryExcess)}</td>
                                            <CurrencyValue
                                                currency="gbp"
                                                currencyDisplay="symbol"
                                                id="CurrencyValue"
                                                showFractions
                                                showGrouping
                                                tag="td"
                                                value={currentVolExcessValue}
                                            />
                                        </tr>
                                        <tr>
                                            <td>{translator(messages.CompulsoryExcess)}</td>
                                            <CurrencyValue
                                                currency="gbp"
                                                currencyDisplay="symbol"
                                                id="CurrencyValue"
                                                showFractions
                                                showGrouping
                                                tag="td"
                                                value={currentCompulsoryExcessValue}
                                            />
                                        </tr>
                                        <tr>
                                            <td>{translator(messages.TotalExcess)}</td>
                                            <CurrencyValue
                                                currency="gbp"
                                                currencyDisplay="symbol"
                                                id="CurrencyValue"
                                                showFractions
                                                showGrouping
                                                tag="td"
                                                value={currentCompulsoryExcessValue + currentVolExcessValue}
                                            />
                                        </tr>
                                    </tbody>
                                </table>
                            </>
                        )}
                    </>
                )}
            </ul>
        );
    }, [translator, changes.coverageAdded, changes.coverageRemoved, changes.breakdownCoverLevel, excessHeaders, changes.pncdPrice, coveragesAdded,
        changes.compulsoryExcess, coveragesRemoved, hasExcessChanges, changes.voluntaryExcess, currentVolExcessValue, currentCompulsoryExcessValue]);

    return (
        <ul className="sg-mta-changes-info-list">
            {hasVehicleChanges && vehicleChanges}
            {hasDriverChanges && driverChanges}
            {hasCoveragesChanges && coveragesChanges}
        </ul>
    );
}

export default SgEndorsementChangesInfo;
